import SearchIcon from '@mui/icons-material/Search'
import { useEffect, useState, useRef } from 'react'
import {
  IconButton,
  Toolbar,
  Tooltip,
  styled,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  Box,
  SelectChangeEvent,
} from '@mui/material'
import { IconFilter, IconBackspace, IconCirclePlus } from '@tabler/icons-react'
import { useDebounce } from 'use-debounce'
import { useNavigate } from 'react-router-dom'
import useInternalUserPermissions from '../../../../../hooks/useInternalUserPermissions'
import { OrderFilterDrawer } from '../OrderFilterDrawer'
import { useDispatch, useSelector } from '../../../../../store/store'
import { dialogToggle } from '../../../../../store/slices/dialog'
import { Search, StyledInputBase } from '../../../../../config'
import { setordersFilterQuerycustomerSearch } from '../../../../../store/slices/filters/filters'
import { setOrdersSearchBy } from '../../../../../store/slices/general'
import { USER_ROLES } from '../../../../../constants/user'

export const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

export const RemoveSearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

export const OrderEnhancedTableToolbar = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const navigate = useNavigate()

  const [drawerOpen, setDrawerOpen] = useState(false)
  const { ordersFilterQuerycustomerSearch } = useSelector((state) => state.filters)
  const { ordersSearchBy } = useSelector((state) => state.general)
  const [searchValue, setSearchValue] = useState(ordersFilterQuerycustomerSearch || '')
  const [debouncedSearchValue] = useDebounce(searchValue, 300)
  const searchInputRef = useRef<HTMLInputElement>(null)
  // determine if user can see the Add button
  const { isInternalUser } = useInternalUserPermissions()
  const { role } = useSelector((state) => state.user.userInfo)
  const allowedCustomerRoles = [
    USER_ROLES.EXTERNAL_RESELLER.value,
    USER_ROLES.EXTERNAL_RESELLER_CUSTOMER.value,
    USER_ROLES.EXTERNAL_DIRECT_CUSTOMER.value,
  ]
  const showAddButton = isInternalUser || allowedCustomerRoles.includes(role || '')

  const searchOptions = [
    { value: 'displayId', label: 'ID (Display ID)' },
    { value: 'customer', label: 'Customer' },
    { value: 'jobName', label: 'Order Name' },
  ]

  useEffect(() => {
    dispatch(setordersFilterQuerycustomerSearch(debouncedSearchValue))
  }, [debouncedSearchValue, dispatch])

  // Focus the search input when search type changes
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [ordersSearchBy])

  const handleOpen = () => {
    dispatch(dialogToggle(true))
    navigate({ search: 'create' })
  }

  const handleSearchTypeChange = (event: SelectChangeEvent<string>) => {
    dispatch(setOrdersSearchBy(event.target.value))
  }

  return (
    <>
      <Toolbar>
        <Box sx={{ flex: '1 1 100%', display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={ordersSearchBy}
              onChange={handleSearchTypeChange}
              displayEmpty
              sx={{ height: '40px' }}
            >
              {searchOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Search sx={{ flex: '1', display: 'flex', alignContent: 'baseline' }}>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              inputRef={searchInputRef}
              onChange={(e) => setSearchValue(e.target.value)}
              value={searchValue}
              placeholder={`Search by ${
                searchOptions.find((opt) => opt.value === ordersSearchBy)?.label || 'field'
              }...`}
              inputProps={{ 'aria-label': 'search' }}
              autoFocus
            />
            {ordersFilterQuerycustomerSearch && (
              <RemoveSearchIconWrapper
                onClick={() => {
                  dispatch(setordersFilterQuerycustomerSearch(''))
                }}
              >
                <IconBackspace size="24px" className="removeIcon" />
              </RemoveSearchIconWrapper>
            )}
          </Search>
        </Box>

        <Tooltip onClick={() => setDrawerOpen(!drawerOpen)} title="Filter list">
          <IconButton>
            <IconFilter size="20px" />
          </IconButton>
        </Tooltip>
        {showAddButton && (
          <Tooltip title="Add new" onClick={handleOpen}>
            <IconButton>
              <IconCirclePlus
                stroke="none"
                size="35px"
                fill={theme.palette.primary.main}
                color={theme.palette.primary.light}
              />
            </IconButton>
          </Tooltip>
        )}

        <OrderFilterDrawer drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
      </Toolbar>
    </>
  )
}
